import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

// Review queue for manually-entered vehicles the DMS feed hasn't adopted.
export default function AdminManualReview() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [lockDays, setLockDays] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const load = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    fetch(`${API_BASE}/admin/manual-vehicles`, { headers: h })
      .then(adminJson)
      .then(d => { setRows(d.vehicles || []); setLockDays(d.lock_days || 10); })
      .catch(() => setError('Failed to load manual vehicles'))
      .finally(() => setLoading(false));
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, kind) => {
    const h = authHeader();
    if (!h) return;
    if (kind === 'delete' && !window.confirm('Remove this manually-entered vehicle? (soft-delete, reversible)')) return;
    setBusyId(id);
    try {
      const url = kind === 'keep'
        ? `${API_BASE}/admin/manual-vehicles/${id}/keep`
        : `${API_BASE}/admin/manual-vehicles/${id}`;
      const res = await fetch(url, { method: kind === 'keep' ? 'POST' : 'DELETE', headers: h });
      if (!res.ok) { setError('Action failed'); return; }
      setRows(rs => rs.filter(r => r.id !== id));
    } catch (e) {
      setError('Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const overCount = rows.filter(r => r.days_old >= lockDays).length;

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <h2>Manual Entry Review {overCount > 0 && <span style={{ color: '#c2410c', fontSize: 15 }}>({overCount} over {lockDays}d)</span>}</h2>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Vehicles a photographer added by hand that the DMS feed hasn't adopted yet. The feed
        normally picks them up within a day or two (they then drop off this list automatically).
        Anything past <b>{lockDays} days</b> (highlighted) needs a look — typo, wrong dealer, or
        a car that sold before it hit the feed. <b>Keep</b> = it's legit, stop flagging it;{' '}
        <b>Remove</b> = soft-delete it (reversible).
      </p>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}

      {loading ? <div>Loading…</div> : rows.length === 0 ? (
        <div style={{ color: '#166534', padding: 20, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          ✓ Nothing to review — every manual entry has been adopted by the feed or already handled.
        </div>
      ) : (
        <table style={table}>
          <thead><tr>{['Days', 'Stock#', 'Vehicle', 'VIN', 'Dealer', 'Photos', ''].map(h =>
            <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(r => {
              const over = r.days_old >= lockDays;
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #eef2f7', background: over ? '#fff7ed' : '#fff' }}>
                  <td style={{ ...td, fontWeight: 700, color: over ? '#c2410c' : '#6b7280' }}>{r.days_old}d</td>
                  <td style={td}>{r.stock_number || '—'}</td>
                  <td style={td}>{[r.year, r.make, r.model].filter(Boolean).join(' ') || '—'}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.vin}</td>
                  <td style={td}>{r.dealership_name || r.dealership_id}</td>
                  <td style={td}>{r.media_count}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => act(r.id, 'keep')} disabled={busyId === r.id} style={keepBtn}>Keep</button>{' '}
                    <button onClick={() => act(r.id, 'delete')} disabled={busyId === r.id} style={delBtn}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const keepBtn = { padding: '5px 12px', background: '#0078D4', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' };
const delBtn = { padding: '5px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer' };
